import BaseAddressEntity from "../Entities/BaseAddressEntity";
import BaseContactEntity from "../Entities/BaseContactEntity";
import ContactMediaEntity from "../Entities/ContactMediaEntity";
import { ContactMediaTypeV3 } from "../Entities/ContactMediaType";
import Util from "../util/Util";

export default abstract class ContactBaseModel {
    private readonly _contactMedia: BaseContactEntity[];
    private readonly _address: BaseAddressEntity[];
    private readonly _contactMediaTypes: ContactMediaEntity[];

    constructor(contactMedia: BaseContactEntity[], address: BaseAddressEntity[], contactMediaTypes: ContactMediaEntity[]) {
        this._contactMedia = contactMedia ? contactMedia : [];
        this._address = address ? address : [];
        this._contactMediaTypes = contactMediaTypes;
    }

    abstract get OrphyLink();
    abstract get DisplayName();
    abstract get Avatar();
    abstract get HasAvatar();
    abstract get HasContactPerson();
    abstract get ContactPerson();
    abstract get CustomId(): string;

    get PreferredEmailAddress(): string {
        return ContactBaseModel.findPreferredEmailAddress(this._contactMedia, this._contactMediaTypes);
    }

    /**
     * Same rule as PreferredEmailAddress, usable without a model instance: the entry
     * flagged as preferred wins, otherwise the first e-mail entry is taken.
     */
    public static findPreferredEmailAddress(contactMedia: BaseContactEntity[], contactMediaTypes: ContactMediaEntity[]): string {
        if (!contactMedia || !contactMediaTypes) {
            return null;
        }

        const emailType = contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.EMail.toString());
        if (!emailType) {
            return null;
        }

        const emails = contactMedia.filter(e => e.MediaId === emailType.Id);
        const preferred = emails.find(e => e.IsPrefered);
        return preferred ? preferred.NameInMedia : Util.firstOrDefault(emails)?.NameInMedia;
    }

    get MailTo(): string {
        return `mailto:${this.PreferredEmailAddress}`;
    }

    get HasEmailAddress(): boolean {
        return !!this.PreferredEmailAddress;
    }

    get CallTo(): string {
        return `tel:${this.PreferredPhoneNumber}`;
    }

    get MapTo(): string {
        if (this.HasAddress) {
            return `https://maps.google.ch?q=${this.FullAddress}`;
        } else {
            return "#";
        }
    }

    get FullAddress(): string {
        if (this.HasAddress) {
            const address = this.PreferredAddress;
            return [address.Street, address.StreetNumber, address.ZipCode, address.City].filter(part => !!part).join(" ");
        }
    }

    get PreferredAddress(): BaseAddressEntity {
        const pAddress = this._address.find(e => e.IsPrefered);
        if (pAddress) {
            return pAddress;
        }
        return Util.firstOrDefault(this._address);
    }

    get PreferredPhoneNumber(): string {
        const phoneNumber = this._contactMedia.find(
            c =>
                (c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Phone.toString()).Id ||
                    c.Id === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Mobile.toString()).Id) &&
                c.IsPrefered
        );
        if (phoneNumber) {
            return phoneNumber.NameInMedia;
        }
        return Util.firstOrDefault(
            this._contactMedia.filter(
                c =>
                    c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Phone.toString()).Id ||
                    c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Mobile.toString()).Id
            )
        )?.NameInMedia;
    }

    get Website(): string {
        const webSite = this._contactMedia.find(c => c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Website.toString()).Id && c.IsPrefered);
        if (webSite) {
            if (webSite.NameInMedia.startsWith("http")) {
                return webSite.NameInMedia;
            } else {
                return "http://" + webSite.NameInMedia;
            }
        }
        const site = Util.firstOrDefault(this._contactMedia.filter(c => c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Website.toString()).Id))?.NameInMedia;
        if (site) {
            if (site.startsWith("http")) {
                return site;
            } else {
                return "http://" + site;
            }
        }
        return null;
    }

    get HasAddress(): boolean {
        const pa = this.PreferredAddress;
        return !!pa && (!!pa.Street || !!pa.ZipCode || !!pa.City);
    }

    get HasPhoneNumber(): boolean {
        return !!this._contactMedia.filter(
            c =>
                c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Phone.toString()).Id ||
                c.Id === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Mobile.toString()).Id
        ).length;
    }

    get HasWebsite(): boolean {
        return !!this._contactMedia.filter(c => c.MediaId === this._contactMediaTypes.find(c => c.MediaType === ContactMediaTypeV3.Website.toString()).Id).length;
    }
}
